/**
 * StationCard — Active/busy station card for SupervisorStationManagement.
 * Shows station header, operator/packer info, progress bar, details,
 * printer assignment, material type restriction, and action buttons.
 * Extracted from SupervisorStationManagement.tsx for maintainability.
 */
import React from 'react';
import { Printer } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getStatusBadgeClass, getStatusDotClass } from '../../../utils/statusColors';
import { StationWithDetails } from '../../../services/stationManagementService';
import featureFlags from '../../../config/featureFlags';

interface StationCardProps {
  station: StationWithDetails;
  stations: StationWithDetails[];
  stationMaterialTypes: Record<number, string>;
  registeredPrinters: Array<{ setting_key: string; setting_value?: string }>;
  onToggleStation: (station: StationWithDetails) => void;
  onAssignSession: (station: StationWithDetails) => void;
  onAssignOperator: (station: StationWithDetails) => void;
  onStartSession: (station: StationWithDetails) => void;
  onPauseSession: (station: StationWithDetails) => void;
  onResumeSession: (station: StationWithDetails) => void;
  onCompleteSession: (station: StationWithDetails) => void;
  onUnplanSession: (station: StationWithDetails) => void;
  onMaterialTypeChange: (stationId: number, newType: string) => void;
  onAssignPrinter: (stationId: number, printerMacId: string | null) => void;
  toggleStationLoading: boolean;
  startSessionLoading: boolean;
  pauseSessionLoading: boolean;
  resumeSessionLoading: boolean;
  completeSessionLoading: boolean;
  unplanSessionLoading: boolean;
}

const StationCard: React.FC<StationCardProps> = ({
  station,
  stations,
  stationMaterialTypes,
  registeredPrinters,
  onToggleStation,
  onAssignSession,
  onAssignOperator,
  onStartSession,
  onPauseSession,
  onResumeSession,
  onCompleteSession,
  onUnplanSession,
  onMaterialTypeChange,
  onAssignPrinter,
  toggleStationLoading,
  startSessionLoading,
  pauseSessionLoading,
  resumeSessionLoading,
  completeSessionLoading,
  unplanSessionLoading,
}) => {
  const { t } = useLanguage();

  const sessionStatus = station.session_status || station.station_status;
  const isActive = sessionStatus === 'active' || sessionStatus === 'in_progress';
  const isPaused = sessionStatus === 'paused';
  const isReady = sessionStatus === 'ready';
  const isPlanned = sessionStatus === 'received';

  // Determine card styling based on status
  const getCardStyle = () => {
    if (station.enabled === false) return 'opacity-50 bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700';
    if (isActive) return 'border-green-400 dark:border-green-600 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 shadow-lg shadow-green-100 dark:shadow-green-900/20';
    if (isReady) return 'border-cyan-400 dark:border-cyan-600 bg-gradient-to-br from-cyan-50 to-white dark:from-cyan-900/20 dark:to-gray-800';
    if (isPlanned) return 'border-purple-300 dark:border-purple-700 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-gray-800';
    if (isPaused) return 'border-yellow-400 dark:border-yellow-600 bg-gradient-to-br from-yellow-50 to-white dark:from-yellow-900/20 dark:to-gray-800';
    return 'border-gray-300 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900/20 dark:to-gray-800';
  };

  // Status indicator dot color (uses session status, not station status)
  const statusDotColor = station.enabled === false
    ? 'bg-gray-400'
    : getStatusDotClass(sessionStatus);

  return (
    <div
      className={`relative border-2 rounded-lg p-4 transition-all ${getCardStyle()}`}
    >
      {/* Status indicator bar on left edge */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${statusDotColor}`} />

      {/* Station Header */}
      <div className="flex items-start justify-between mb-2 pl-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${statusDotColor} ${isActive ? 'animate-pulse' : ''}`} />
            <h3 className="font-semibold text-gray-900 dark:text-white">{station.station_name}</h3>
            {station.enabled === false && (
              <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                {t('supervisor.disabled')}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 pl-5">
            #{station.station_id} • {station.station_type}
            {station.printer_mac_id && (
              <span className="ml-1 text-green-500" title={`Printer: ${station.printer_mac_id}`}>🖨</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Enable/Disable Toggle */}
          <button
            onClick={() => onToggleStation(station)}
            disabled={toggleStationLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              station.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
            title={station.enabled ? t('supervisor.clickToDisable') : t('supervisor.clickToEnable')}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                station.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${station.enabled === false ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400' : getStatusBadgeClass(station.session_status || station.station_status)}`}>
            {station.enabled === false ? t('supervisor.disabled') : t(`status.${station.session_status || station.station_status}`) || station.session_status || station.station_status}
          </span>
        </div>
      </div>

      {/* Operator & Packer */}
      <div className="mb-3 text-sm pl-2 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400 text-xs w-16">{t('supervisor.operator')}:</span>
          <span className={`${(station.current_operator_name || station.assigned_operator_name) ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-400 dark:text-gray-500 italic'}`}>
            {station.current_operator_name || station.assigned_operator_name || t('supervisor.notAssigned')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400 text-xs w-16">{t('supervisor.packer')}:</span>
          <span className={`${station.current_packer_name ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-400 dark:text-gray-500 italic'}`}>
            {station.current_packer_name || t('supervisor.notAssigned')}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      {station.session_number && station.total_boxes > 0 && (() => {
        const targetBoxes = Number(station.total_boxes) || 0;
        const producedBoxes = Number(station.boxes_produced) || 0;
        const progress = targetBoxes > 0 ? Math.min((producedBoxes / targetBoxes) * 100, 100) : 0;
        const isCompleted = station.session_status === 'completed';

        return (
          <div className="mb-3 pl-2">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600 dark:text-gray-400">{t('supervisor.progress')}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {producedBoxes} / {targetBoxes} {t('supervisor.boxes')} ({progress.toFixed(2)}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  isCompleted ? 'bg-gradient-to-r from-green-400 to-green-600' :
                  progress > 75 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                  progress > 50 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                  progress > 25 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                  'bg-gradient-to-r from-gray-400 to-gray-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Station Details */}
      <div className="space-y-2 text-sm pl-2">
        <div>
          <div className="text-gray-600 dark:text-gray-400">{t('supervisor.rawMaterial')}:</div>
          <div className={`font-medium ${station.raw_material_item_number ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'}`}>
            {station.raw_material_item_number ? (
              <>
                <div>{station.raw_material_name || station.raw_material_item_number}</div>
                {station.raw_material_name && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">{station.raw_material_item_number}</div>
                )}
              </>
            ) : t('supervisor.notAssigned')}
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
          <div>
            <div className="text-gray-600 dark:text-gray-400">{t('supervisor.finishedProduct')}:</div>
            <div className={`font-medium ${(station.product_name || station.product_sku) ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'}`}>
              {(station.product_name || station.product_sku) ? (
                <>
                  <div>{station.product_name || station.product_sku}</div>
                  {station.product_sku && <div className="text-xs text-gray-500 dark:text-gray-400">{station.product_sku}</div>}
                </>
              ) : t('supervisor.notAssigned')}
            </div>
          </div>
        </div>
      </div>

      {/* Printer Assignment Dropdown */}
      {registeredPrinters.length > 0 && (
        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700 ml-2">
          <label className="flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            <Printer className="w-3 h-3" />
            {t('stations.assignPrinter')}
          </label>
          <select
            value={station.printer_mac_id || ''}
            onChange={(e) => onAssignPrinter(station.station_id, e.target.value || null)}
            className="w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('stations.printerNotAssigned')}</option>
            {registeredPrinters.map((p) => {
              // Show which station already has this printer (if any other station)
              const otherStation = stations?.find(
                (s) => s.printer_mac_id === p.setting_key && s.station_id !== station.station_id
              );
              return (
                <option key={p.setting_key} value={p.setting_key}>
                  {p.setting_key}{otherStation ? ` (${otherStation.station_name})` : ''}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Material Type Restriction (feature flag controlled) */}
      {featureFlags.MATERIAL_TYPE_RESTRICTION && (
        <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/30 rounded border border-purple-200 dark:border-purple-700 ml-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('supervisor.allowedMaterialType')}
          </label>
          <select
            value={stationMaterialTypes[station.station_id] || ''}
            onChange={(e) => onMaterialTypeChange(station.station_id, e.target.value)}
            className="w-full px-2 py-1 text-sm border border-purple-300 dark:border-purple-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">{t('supervisor.selectMaterialType')}</option>
            <option value="Chicken">{t('supervisor.materialTypeChicken')}</option>
            <option value="Beef">{t('supervisor.materialTypeBeef')}</option>
            <option value="Pork">{t('supervisor.materialTypePork')}</option>
            <option value="Goat/Lamb">{t('supervisor.materialTypeGoatLamb')}</option>
          </select>
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2 pl-2">
        {!station.session_number && (
          <button
            onClick={() => onAssignSession(station)}
            className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
          >
            {t('supervisor.assignSession')}
          </button>
        )}

        {/* Assign Operator button */}
        {station.session_number && !station.assigned_operator && ['received', 'ready'].includes(station.session_status || '') && (
          <button
            onClick={() => onAssignOperator(station)}
            className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded hover:bg-orange-600 transition-colors"
          >
            {t('supervisor.assignOperator')}
          </button>
        )}

        {station.session_number && station.session_status === 'ready' && (
          <button
            onClick={() => onStartSession(station)}
            className={`w-full px-4 py-2 text-white text-sm font-medium rounded transition-colors ${
              station.assigned_operator
                ? 'bg-purple-600 hover:bg-purple-700'
                : 'bg-gray-500 hover:bg-gray-600'
            }`}
            disabled={startSessionLoading}
          >
            {startSessionLoading ? t('supervisor.starting') : t('supervisor.startSession')}
          </button>
        )}

        {station.session_number && ['received', 'ready'].includes(station.session_status || '') && (
          <button
            onClick={() => onUnplanSession(station)}
            className="w-full px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded hover:bg-gray-600 transition-colors"
            disabled={unplanSessionLoading}
          >
            {unplanSessionLoading ? t('supervisor.removingSession') : t('supervisor.removeSession')}
          </button>
        )}

        {station.session_status === 'active' && (
          <>
            <button
              onClick={() => onPauseSession(station)}
              className="w-full px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded hover:bg-yellow-700 transition-colors"
              disabled={pauseSessionLoading}
            >
              {pauseSessionLoading ? t('supervisor.pausing') : t('supervisor.pauseSession')}
            </button>
            <button
              onClick={() => onCompleteSession(station)}
              className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
              disabled={completeSessionLoading}
            >
              {completeSessionLoading ? t('supervisor.completing') : t('supervisor.completeSession')}
            </button>
          </>
        )}

        {station.session_status === 'paused' && (
          <>
            <button
              onClick={() => onResumeSession(station)}
              className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
              disabled={resumeSessionLoading}
            >
              {resumeSessionLoading ? t('supervisor.resuming') : t('supervisor.resumeSession')}
            </button>
            <button
              onClick={() => onCompleteSession(station)}
              className="w-full px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
              disabled={completeSessionLoading}
            >
              {completeSessionLoading ? t('supervisor.completing') : t('supervisor.completeSession')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StationCard;
